import React from "react";
import Link from "next/link";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface StepProps {
  stepNumber: number;
  title: string;
  description: string;
  showTimeline?: boolean;
  isFirst?: boolean;
}

const StepDesktop = ({ stepNumber, title, description, showTimeline = true }: StepProps) => {
  return (
    <div className="flex md:flex-col flex-row gap-6 w-full">
      <div className="flex items-center">
        <div className="shrink-0 w-fit bg-white text-black border border-rebolt-500 rounded-lg py-1 px-4 text-sm font-semibold">
          <p>Step {stepNumber}</p>
        </div>
        {showTimeline && (
          <div className="bg-rebolt-500 h-0.5 w-full relative">
            <div className="absolute top-0 -translate-y-1/2 right-0 w-2 h-2 bg-rebolt-500 rounded-full"></div>
          </div>
        )}
      </div>
      <div className="flex flex-col md:gap-3 gap-1 pr-6">
        <h2 className="font-semibold md:text-3xl text-2xl tracking-tight">{title}</h2>
        <p className="md:text-lg text-sm">{description}</p>
      </div>
    </div>
  );
};

const StepMobile = ({ stepNumber, title, description, showTimeline = true, isFirst = false }: StepProps) => {
  return (
    <div className={cn("flex flex-row gap-6 w-full", isFirst ? "mt-0" : "-mt-8")}>
      <div className="flex items-center">
        {showTimeline && (
          <div className="bg-rebolt-500 h-full w-[3px] relative">
            <div className="z-20 absolute bottom-0 rounded-full flex items-center justify-center left-1/2 -translate-x-1/2 bg-clay-200 p-2.5">
              <div className="w-3.5 h-3.5 bg-rebolt-500 rounded-full"></div>
            </div>
          </div>
        )}
      </div>
      <div className={cn("flex flex-col gap-3 w-full ", showTimeline ? "pb-16" : "")}>
        <div className="shrink-0 w-fit bg-white text-black border border-rebolt-500 rounded-lg py-1 px-4 text-sm font-semibold">
          <p>Step {stepNumber}</p>
        </div>
        <div className="flex flex-col gap-1">
          <h2 className="font-semibold md:text-3xl text-2xl tracking-tight">{title}</h2>
          <p className="md:text-lg text-sm">{description}</p>
        </div>
      </div>
    </div>
  );
};

const HowItWorks = ({ ctaLink }: { ctaLink: string }) => {
  return (
    <div className="bg-clay-200 text-black px-5 md:px-16 md:py-28 py-16">
      <div className="flex flex-col justify-between md:gap-20 gap-10 max-w-7xl mx-auto relative">
        <div className="flex gap-6 justify-between items-center md:flex-row flex-col">
          <div className="flex flex-col md:gap-2 gap-5 font-semibold max-w-3xl">
            <div className="flex flex-col gap-3 md:gap-2">
              <p>How it works</p>
              <div className="flex flex-col md:gap-2 gap-5">
                <h1 className="font-clash font-semibold text-4xl md:text-5xl leading-[1.2] tracking-tight">
                  Getting started with Rebolt is easy
                </h1>
                <p className="font-normal text-base md:text-lg">
                  Three quick steps to launch your website and start seeing results — simple, direct, and designed to
                  save you time.
                </p>
              </div>
            </div>
          </div>
          <Link href={ctaLink} target="_blank" rel="noopener noreferrer" className="w-full md:w-fit">
            <Button variant="rebolt-primary" className="h-fit py-3! px-6! w-full md:w-fit">
              Book a demo call
            </Button>
          </Link>
        </div>

        <div className="flex flex-col md:flex-row w-full gap-8">
          <div className="hidden md:flex flex-col md:flex-row w-full gap-8">
            <StepDesktop
              stepNumber={1}
              title="Schedule a demo call"
              description="Meet our team, explore your goals, and see how Rebolt helps your business grow."
            />
            <StepDesktop
              stepNumber={2}
              title="Review initial website"
              description="Our AI builds your site fast. You review, request edits, and approve it for launch."
            />
            <StepDesktop
              stepNumber={3}
              title="Onboard to Rebolt"
              description="We set up your tools, connect your socials, and get your website live — fully managed."
              showTimeline={false}
            />
          </div>
          <div className="md:hidden flex flex-col w-full">
            <StepMobile
              stepNumber={1}
              title="Schedule a demo call"
              description="Meet our team, explore your goals, and see how Rebolt helps your business grow."
              isFirst={true}
            />
            <StepMobile
              stepNumber={2}
              title="Review initial website"
              description="Our AI builds your site fast. You review, request edits, and approve it for launch."
            />
            <StepMobile
              stepNumber={3}
              title="Onboard to Rebolt"
              description="We set up your tools, connect your socials, and get your website live — fully managed."
              showTimeline={false}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default HowItWorks;
